// /CalculationsScreen/components/ProductCalculationScreen/index.tsx
import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import RoomHeader from '../../utils/RoomHeader';
import { formatNumber, formatDateTime, formatTimeWithError, formatDurationMinutes } from '../../utils/formatters';
import classes from './productCalculationTable.module.scss';
import { useStore } from 'hooks/useStore';
import { ProductCalculationsResponse } from 'Interfaces/CalculationInterfaces/MainCalculationInterfaces';

interface ProductCalculationScreenProps {
  roomData: ProductCalculationsResponse;
  loading: boolean;
  expandedRows: any;
  setExpandedRows: (rows: any) => void;
}

// Body renderer functions defined outside component
const renderBottlesRequired = (rowData: any) => formatNumber(rowData.bottlesRequired);

const renderProjectedBpm = (rowData: any) => formatNumber(rowData.projectedBpm);

const renderActualBpm = (rowData: any) => formatNumber(rowData.actualBpm);

const renderPillCount = (rowData: any) => formatNumber(rowData.pillCount);

const renderStartTime = (rowData: any) => formatDateTime(rowData.startTime);

const renderDurationMinutes = (rowData: any) => {
  const value = formatNumber(rowData.durationMinutes);
  return rowData.durationMinutes === 0 ? (
    <span style={{ color: 'red' }}>Error</span>
  ) : (
    value
  );
};

const ProductCalculationScreen: React.FC<ProductCalculationScreenProps> = ({
  roomData,
  loading,
  expandedRows,
  setExpandedRows
}) => {
  const [expandedRooms, setExpandedRooms] = useState<Record<number, boolean>>({});
  const { roomStore } = useStore();

  // Add a state to track if we've received a response
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);

  // Reset the response flag when loading changes to true
  useEffect(() => {
    if (loading === true) {
      setHasReceivedResponse(false);
    }
  }, [loading]);

  // Set the response flag when we receive data (when loading becomes false)
  useEffect(() => {
    if (loading === false) {
      setHasReceivedResponse(true);
    }
  }, [loading]);

  // Ensure room data is loaded
  useEffect(() => {
    if (roomStore.rooms.length === 0) {
      roomStore.fetchRooms(1, 100);
    }
  }, []);

  // Expand all accordions whenever roomData changes
  useEffect(() => {
    if (roomData && roomData.length > 0) {
      // Create object with all rooms expanded
      const expandAll: Record<number, boolean> = {};
      roomData.forEach(room => {
        expandAll[room.id] = true;
      });
      setExpandedRooms(expandAll);
    }
  }, [roomData]);

  // Check if roomData has calculations property
  const hasValidData = () => {
    return (
      roomData &&
      roomData.length > 0 &&
      roomData.some(room => room.calculations && room.calculations.length > 0)
    );
  };

  // Toggle room expansion
  const toggleRoom = (roomId: number) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // Show loading spinner when data is being fetched or when we haven't received a response
  if (loading === true || !hasReceivedResponse) {
    return (
      <div className={classes.loadingContainer}>
        <ProgressSpinner className={classes.spinner} />
        <div>Loading calculation data...</div>
      </div>
    );
  }

  // Only show "No data found" when we've received a response but it's empty
  if (hasReceivedResponse && !hasValidData()) {
    return (
      <div className={classes.emptyMessage}>
        No calculation data found. Please check your filters or try again later.
      </div>
    );
  }


  // Render the room data with expandable rows
  return (
    <div className={classes.productCalculationContainer}>
      {roomData.map(room => (
        <div key={room.id} className={classes.roomSection}>
          <RoomHeader
            name={room.name}
            roomId={room.id}
            isExpanded={!!expandedRooms[room.id]}
            onToggle={() => toggleRoom(room.id)}
          />
          {expandedRooms[room.id] && (
            <DataTable
              value={room.calculations}
              emptyMessage="No records found"
              className={classes.dataTable}
              rowClassName={() => classes.tableRow}
              resizableColumns
              columnResizeMode="expand"
              scrollable
              scrollHeight="flex"
            >
              <Column field="jobId" header="Job" className={classes.column} />
              <Column field="description" header="Drug" className={classes.column} />
              <Column field="drugReferenceFgNDC" header="NDC" className={classes.column} />
              <Column field="drugReferenceConceptCode" header="NDC 8" className={classes.column} />
              <Column
                field="bottlesRequired"
                header="Bottles Required"
                className={classes.column}
                body={renderBottlesRequired}
              />
              <Column
                field="projectedBpm"
                header="BPM (Projected)"
                className={classes.column}
                body={renderProjectedBpm}
              />
              <Column
                field="actualBpm"
                header="BPM (Actual)"
                className={classes.column}
                body={renderActualBpm}
              />
              <Column
                field="pillCount"
                header="Pill Count"
                className={classes.column}
                body={renderPillCount}
              />
              <Column
                field="startTime"
                header="Start Time"
                className={classes.column}
                body={renderStartTime}
              />
              <Column
                field="durationMinutes"
                header="Minutes"
                className={classes.column}
                body={rowData => {
                  const value = formatDurationMinutes(rowData.durationMinutes);
                  return rowData.durationMinutes === 0 ? (
                    <span style={{ color: 'red' }}>Error</span>
                  ) : (
                    value
                  );
                }}
              />
              <Column field="room" header="Room" className={classes.column} />
              <Column field="runType" header="Run Type" className={classes.column} />
            </DataTable>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProductCalculationScreen;
